import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const App = () => {
  const audio = useRef(
    new Audio(process.env.PUBLIC_URL + "/Metal_Gong-Dianakc-109711828.mp3")
  ).current;
  const [params] = useState(new URLSearchParams(window.location.search));
  const [roundLength] = useState(Number(params.get("duration")));
  const [numRounds] = useState(Number(params.get("rounds")));

  const initialState = useMemo(
    () => ({
      currentRound: 1,
      secondsRemaining: roundLength,
      isRunning: true,
    }),
    [roundLength]
  );
  const [{ currentRound, secondsRemaining, isRunning }, setState] =
    useState(initialState);

  const totalTimeElapsed = useMemo(
    () => (currentRound - 1) * roundLength + (roundLength - secondsRemaining),
    [currentRound, roundLength, secondsRemaining]
  );

  const totalTimeRemaining = useMemo(
    () => numRounds * roundLength - totalTimeElapsed,
    [numRounds, roundLength, totalTimeElapsed]
  );

  const tick = useCallback(() => {
    setState((state) => {
      if (!state.isRunning) {
        return state;
      }

      if (state.secondsRemaining > 1) {
        return {
          ...state,
          secondsRemaining: state.secondsRemaining - 1,
        };
      } else if (state.currentRound < numRounds) {
        audio.play();
        return {
          ...state,
          currentRound: state.currentRound + 1,
          secondsRemaining: roundLength,
        };
      } else if (state.currentRound === numRounds) {
        (async () => {
          audio.play();
          await new Promise((resolve) => setTimeout(resolve, 1000));
          for (let i = 4; i >= 0; i -= 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            audio.volume = i * 0.2;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
          audio.currentTime = 0;
          audio.volume = 1;
          await new Promise((resolve) => setTimeout(resolve, 1000));
          for (let i = 4; i >= 0; i -= 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            audio.volume = i * 0.2;
          }
          await new Promise((resolve) => setTimeout(resolve, 100));
          audio.currentTime = 0;
          audio.volume = 1;
        })();
        return {
          ...state,
          secondsRemaining: state.secondsRemaining - 1,
          isRunning: false,
        };
      } else {
        throw new Error("Invalid state");
      }
    });
  }, [audio, numRounds, roundLength]);

  useEffect(() => {
    if (
      !Number.isFinite(roundLength) ||
      roundLength < 1 ||
      !Number.isFinite(numRounds) ||
      numRounds < 1
    ) {
      return;
    }

    if (isRunning) {
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }
  }, [isRunning, numRounds, roundLength, tick]);

  return (
    <div className="h-screen w-screen dark:bg-slate-800 dark:text-white font-mono">
      <div className="flex flex-col justify-center items-center h-full">
        <div className="border rounded-lg px-12 py-8 border-slate-800 dark:border-white">
          {!Number.isFinite(roundLength) ||
          roundLength < 1 ||
          !Number.isFinite(numRounds) ||
          numRounds < 1 ? (
            <p className="max-w-md leading-8">
              Specify a timer with query params{" "}
              <span className="bg-cyan-400 dark:bg-cyan-800 px-2 py-1 rounded">
                duration
              </span>{" "}
              and{" "}
              <span className="bg-cyan-400 dark:bg-cyan-800 px-2 py-1 rounded">
                rounds
              </span>
              .
              <br />
              For example:{" "}
              <a
                className="hover:underline"
                href={process.env.PUBLIC_URL + "?duration=60&rounds=3"}
              >
                <span className="bg-cyan-400 dark:bg-cyan-800 px-2 py-1 rounded">
                  ?duration=60&rounds=3
                </span>
              </a>{" "}
              starts a 60 second timer which counts down 3 times.
            </p>
          ) : (
            <>
              <div className="flex flex-row justify-center mb-8">
                <div className="text-9xl font-bold">
                  <Clock
                    seconds={Math.max(secondsRemaining - 1, 0)}
                    isRunning={isRunning}
                  />
                </div>
              </div>
              <div className="flex flex-row gap-8 mb-8 justify-center">
                <div className="flex flex-col">
                  <span className="">Elapsed:</span>
                  <div className="text-3xl font-bold">
                    <Clock seconds={totalTimeElapsed} isRunning={isRunning} />
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="">Round:</span>
                  <div className="text-3xl font-bold">
                    {currentRound}/{numRounds}
                  </div>
                </div>
                <div className="flex flex-col">
                  <span className="">Remaining:</span>
                  <div className="text-3xl font-bold">
                    <Clock seconds={totalTimeRemaining} isRunning={isRunning} />
                  </div>
                </div>
              </div>
              <div className="flex flex-row gap-8 justify-center">
                <Button
                  className="w-24"
                  onClick={() => {
                    setState((state) => {
                      if (
                        state.secondsRemaining === 0 &&
                        state.currentRound === numRounds &&
                        !state.isRunning
                      ) {
                        return state;
                      }

                      return {
                        ...state,
                        isRunning: !state.isRunning,
                      };
                    });
                  }}
                  disabled={
                    secondsRemaining === 0 &&
                    currentRound === numRounds &&
                    !isRunning
                  }
                >
                  {isRunning ? "Pause" : "Resume"}
                </Button>
                <Button
                  onClick={() => {
                    setState(initialState);
                  }}
                >
                  Restart
                </Button>
                <Button
                  onClick={() => {
                    setState((state) => {
                      if (state.isRunning) {
                        if (state.currentRound < numRounds) {
                          return {
                            ...state,
                            currentRound: state.currentRound + 1,
                            secondsRemaining: roundLength,
                          };
                        } else {
                          return {
                            ...state,
                            secondsRemaining: 0,
                            isRunning: false,
                          };
                        }
                      }
                      return state;
                    });
                  }}
                  disabled={!isRunning}
                >
                  End Round
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Button: React.FC<
  React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > & { children: ReactNode; onClick: () => void }
> = ({ children, onClick, disabled, className, ...otherProps }) => (
  <button
    onClick={onClick}
    className={
      "border-2 font-bold py-1 px-4 rounded border-slate-800 dark:border-white" +
      (disabled ? " text-gray-400" : "") +
      (className ? " " + className : "")
    }
    disabled={disabled}
    {...otherProps}
  >
    {children}
  </button>
);

const Clock: React.FC<{ seconds: number; isRunning: boolean }> = ({
  seconds,
  isRunning,
}) => (
  <span className={isRunning ? "" : "text-gray-400"}>
    {Math.floor(seconds / 60)}:{seconds % 60 < 10 ? "0" : ""}
    {seconds % 60}
  </span>
);

export default App;
